import { useEffect, useState } from 'react';
import { Button, Groupper, Header, Icon, Message, Modal } from 'react-frontier';
import { useNavigate } from 'react-router-dom';
import API from '../API';
import { BankAccount } from '../Classes';
import { formatCreditCard, maskCardNumber, maskCLABE } from '../Util';

var BankAccounts =()=>{
	var navigate = useNavigate();
	var [accounts, setAccounts] = useState<BankAccount[] | null>(null);
	var [loadError, setLoadError] = useState<string | null>(null);
	var [detailAccount, setDetailAccount] = useState<BankAccount | null>(null);
	var [selectedAccount, setSelectedAccount] = useState<BankAccount | null>(null);
	var [primaryLoadingId, setPrimaryLoadingId] = useState<number | null>(null);
	var [primaryError, setPrimaryError] = useState<string | null>(null);
	var [deleteLoading, setDeleteLoading] = useState(false);
	var [deleteError, setDeleteError] = useState<string | null>(null);
	var menuItemStyle = {
		fontSize: 15,
		paddingTop: 12,
		paddingBottom: 12,
	};

	var load =()=>{
		setLoadError(null);
		setPrimaryError(null);
		API.listBankAccounts().then((res)=>{
			if(res.error) return setLoadError(res.message || 'Error al cargar tus cuentas.');
			setAccounts(res.data || []);
		}).catch(()=>{
			setLoadError('Error inesperado al cargar tus cuentas.');
		});
	};

	useEffect(()=>{
		load();
	}, []);

	var onSelectPrimary =(account_id: number)=>{
		setPrimaryLoadingId(account_id);
		setPrimaryError(null);
		API.setPrimaryBankAccount(account_id).then((res)=>{
			if(res.error) return setPrimaryError(res.message || 'No se pudo seleccionar la cuenta principal.');
			setAccounts(prev => (prev || []).map(account => ({
				...account,
				is_primary: account.account_id === account_id,
			})));
		}).catch(()=>{
			setPrimaryError('Hubo un error inesperado al seleccionar la cuenta principal.');
		}).finally(()=>{
			setPrimaryLoadingId(null);
		});
	};

	var onDeleteAccount =()=>{
		if(!selectedAccount?.account_id) return;

		setDeleteLoading(true);
		setDeleteError(null);
		API.deleteBankAccount(selectedAccount.account_id).then((res)=>{
			if(res.error) return setDeleteError(res.message || 'No se pudo eliminar la cuenta.');
			setAccounts(prev => (prev || []).filter(account => account.account_id !== selectedAccount.account_id));
			setSelectedAccount(null);
		}).catch(()=>{
			setDeleteError('Hubo un error inesperado eliminando la cuenta.');
		}).finally(()=>{
			setDeleteLoading(false);
		});
	};

	if(loadError) return <div className="team-check-screen">
		<Header
			centered
			text="No pudimos cargar tus cuentas"
			subtext={loadError}
			iconName="face-frown-open"
			actions={<Button type="button" text="Reintentar" color="black" onClick={load} />}
		/>
	</div>;

	if(!accounts) return <div className="team-check-screen">
		<Header centered loading subtext="Cargando cuentas" />
	</div>;

	return <div className="team-check-screen">
		<Groupper
			title="Mis Cuentas"
			titleSize="small"
			style={{ margin: 0 }}
			actions={
				<div className="team-groupper-actions">
					<Button type="button" text="Regresar" basic onClick={()=>navigate('/profile')} />
				</div>
			}
			titleRight={
				<Button type="button" size='tiny' text="Agregar" color="orange" iconName="plus" onClick={()=>navigate('/profile/accounts/add')} />
			}
		>
			{accounts.length === 0 ? (
				<Header
					centered
					size="small"
					text="No hay cuentas registradas"
					subtext="Agrega una cuenta bancaria."
					iconName="credit-card"
					style={{ marginTop: 12, marginBottom: 4 }}
				/>
			) : (
				<div className="team-bank-list">
					{primaryError ? <Message type="error" text={primaryError} /> : null}
					{accounts.map(account=>{
						var isSpei = !!account.clabe;
						var accountLabel = isSpei ? `SPEI ${maskCLABE(account.clabe || '')}` : `Tarjeta ${maskCardNumber(account.card_number || '')}`;

						return <div key={account.account_id} className="team-bank-card">
							<div className="team-bank-card-top">
								<div className="team-bank-card-icon">
									<Icon name="credit-card" size={18} style={{ margin: 0 }} />
								</div>
								<div className="team-bank-card-main">
									<div className="team-bank-card-type">
										<span>{accountLabel}</span>
										{account.is_primary ? <Icon name="circle-check" color="green" size={20} /> : null}
									</div>
									<div className="team-bank-card-owner">{account.name_account}</div>
								</div>
							</div>
							<div className="team-bank-card-meta">
								<div className="team-bank-card-bank">{account.bank || 'Banco no identificado'}</div>
								<div className="team-bank-card-actions">
									{account.postal_code && <div className="team-bank-card-extra">CP {account.postal_code}</div>}
									<Button
										icon
										type="button"
										color="orange"
										size="small"
										iconName="ellipsis-h"
										disabled={primaryLoadingId !== null}
										menu={[
											{
												text: account.is_primary ? 'Cuenta principal' : 'Seleccionar principal',
												iconName: account.is_primary ? 'circle-check' : 'circle',
												style: menuItemStyle,
												onClick: ()=>{
													if(account.is_primary) return;
													onSelectPrimary(account.account_id);
												}
											},
											{ separator: true },
											{
												text: 'Ver detalle',
												iconName: 'credit-card',
												style: menuItemStyle,
												onClick: ()=>{
													setDetailAccount(account);
												}
											},
											{
												text: 'Eliminar',
												iconName: 'trash',
												style: menuItemStyle,
												onClick: ()=>{
													setPrimaryError(null);
													setDeleteError(null);
													setSelectedAccount(account);
												}
											}
										]}
									/>
								</div>
							</div>
						</div>;
					})}
				</div>
			)}
		</Groupper>
		<Modal open={!!detailAccount} onClose={()=> setDetailAccount(null)} size="tiny">
			<Modal.Header>Detalle de cuenta</Modal.Header>
			<Modal.Content>
				<Header
					size="small"
					text={detailAccount ? (detailAccount.clabe || formatCreditCard(detailAccount.card_number || '')) : ''}
					subtext={detailAccount?.clabe ? 'CLABE interbancaria' : 'Número de tarjeta'}
				/>
			</Modal.Content>
			<Modal.Actions>
				<Button type="button" text="Cerrar" basic onClick={()=> setDetailAccount(null)} />
			</Modal.Actions>
		</Modal>
		<Modal open={!!selectedAccount} onClose={()=> !deleteLoading && setSelectedAccount(null)} size="tiny">
			<Modal.Header>Eliminar cuenta</Modal.Header>
			<Modal.Content>
				<Header
					size="small"
					text="¿Deseas eliminar esta cuenta?"
					subtext={selectedAccount ? (selectedAccount.clabe ? `SPEI ${maskCLABE(selectedAccount.clabe || '')}` : `Tarjeta ${maskCardNumber(selectedAccount.card_number || '')}`) : ''}
				/>
				{deleteError ? <Message type="error" text={deleteError} style={{ marginTop: 15 }} /> : null}
			</Modal.Content>
			<Modal.Actions>
				<Button type="button" text="Cancelar" basic onClick={()=> setSelectedAccount(null)} disabled={deleteLoading} />
				<Button type="button" text="Eliminar" color="red" onClick={onDeleteAccount} loading={deleteLoading} />
			</Modal.Actions>
		</Modal>
	</div>;
};

export default BankAccounts;
